import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Repeat, Plus, Pencil, Trash2, Download, Syringe, Target, HelpCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "../lib/csv";
import ProductTour from "../components/ProductTour";
import CycleTemplatesPicker from "../components/CycleTemplatesPicker";
import PageHero from "../components/PageHero";

const CYCLE_TOUR_KEY = "amino_cycle_tour_seen_v1";
const CYCLE_TOUR_STEPS = [
  {
    placement: "center",
    title: "How to start a cycle",
    body: "Cycles are your run plans. The Dashboard tracks progress + adherence against actual shots you log. Let's set one up.",
  },
  {
    selector: '[data-testid="cycle-peptide"]',
    placement: "bottom",
    title: "1. Peptide",
    body: "Which compound is this cycle for? Only one peptide per cycle — start a second cycle if you're stacking.",
  },
  {
    selector: '[data-testid="cycle-start"]',
    placement: "bottom",
    title: "2. Start & End dates",
    body: "Start date drives your calendar progress bar. End date is optional — leave blank for an open-ended cycle.",
  },
  {
    selector: '[data-testid="cycle-dose"]',
    placement: "bottom",
    title: "3. Dose per shot",
    body: "Milligrams per injection. Used with Frequency to compute expected weekly total and adherence.",
  },
  {
    selector: '[data-testid="cycle-frequency"]',
    placement: "top",
    title: "4. Frequency",
    body: "Free-form text like `2x/week`, `daily`, `M/W/F`, or `EOD`. This label shows on the Dashboard card.",
  },
  {
    selector: '[data-testid="cycle-weekly-target"]',
    placement: "top",
    title: "5. Weekly Target (optional)",
    body: "Override the auto-computed weekly mg goal. Useful for tapered/loaded protocols. Adherence % measures actual shots vs this.",
  },
  {
    selector: '[data-testid="cycle-active"]',
    placement: "top",
    title: "6. Active toggle",
    body: "Only active cycles surface on the Dashboard. Toggle off when a cycle ends — the card + history are preserved.",
  },
  {
    selector: '[data-testid="cycle-save"]',
    placement: "top",
    title: "7. Save",
    body: "Locks it in. The cycle appears in this grid and — if active — on your Dashboard with live progress + adherence.",
  },
];

const empty = { peptide_name: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", dose_mg: "", frequency: "", weekly_target_mg: "", notes: "", active: true };

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function daysBetween(a, b) {
  return Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}

// Convert dose to mg
function toMg(amount, unit) {
  if (!amount) return 0;
  if (unit === "mg") return amount;
  if (unit === "mcg") return amount / 1000;
  return 0; // IU / mL — skip
}

// Frequency string → shots-per-day multiplier
function parseFrequency(freq) {
  if (!freq) return null;
  const f = String(freq).toLowerCase().trim();
  if (/^daily$|^qd$|^every\s+day$|^1\s*x\s*\/\s*day$/.test(f)) return 1;
  if (/^eod$|^every\s+other\s+day$|^q2d$/.test(f)) return 0.5;
  if (/^weekly$|^qw$|^1\s*x\s*\/\s*week$/.test(f)) return 1 / 7;
  const perWeek = f.match(/(\d+(?:\.\d+)?)\s*x\s*\/?\s*week/);
  if (perWeek) return parseFloat(perWeek[1]) / 7;
  const perDay = f.match(/(\d+(?:\.\d+)?)\s*x\s*\/?\s*day/);
  if (perDay) return parseFloat(perDay[1]);
  const everyN = f.match(/^q\s*(\d+)\s*d$|every\s+(\d+)\s+days?/);
  if (everyN) {
    const n = parseFloat(everyN[1] || everyN[2]);
    if (n > 0) return 1 / n;
  }
  return null;
}

// Compute shots-based progress for a cycle
function computeCycleProgress(cycle, shots) {
  // Tolerance: shot's datetime-local input truncates to the minute, so a shot logged in
  // the same minute as the cycle was created can end up a few seconds *before* start_date.
  // Grant a 60-second tolerance so those shots still count.
  const start = new Date(cycle.start_date).getTime() - 60_000;
  const now = Date.now();
  const end = cycle.end_date ? new Date(cycle.end_date).getTime() : null;
  const windowEnd = Math.min(end ?? now, now);

  const cycleShots = (shots || []).filter((s) => {
    if (s.peptide_name !== cycle.peptide_name) return false;
    const t = new Date(s.taken_at).getTime();
    return t >= start && t <= (end ?? now + 1);
  });

  const shotsTaken = cycleShots.length;
  const mgDosed = cycleShots.reduce((acc, s) => acc + toMg(s.dose_amount, s.dose_unit), 0);

  const elapsedDays = Math.max(0, (windowEnd - start) / 86400000);
  const totalDays = end ? Math.max(0, (end - start) / 86400000) : null;

  // Determine expected shots for progress
  const rate = parseFrequency(cycle.frequency); // shots per day
  let expectedShotsSoFar = null;
  let expectedShotsTotal = null;
  if (rate != null) {
    expectedShotsSoFar = elapsedDays * rate;
    if (totalDays != null) expectedShotsTotal = totalDays * rate;
  }

  // Adherence pct = actual / expected so far
  let adherencePct = null;
  if (expectedShotsSoFar != null && expectedShotsSoFar > 0) {
    adherencePct = Math.min(200, Math.round((shotsTaken / expectedShotsSoFar) * 100));
  }

  // Overall cycle completion — prefer shots-based, else dates
  let completionPct = null;
  if (expectedShotsTotal != null && expectedShotsTotal > 0) {
    completionPct = Math.min(100, Math.round((shotsTaken / expectedShotsTotal) * 100));
  } else if (totalDays != null && totalDays > 0) {
    completionPct = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
  }

  return {
    shotsTaken,
    mgDosed,
    expectedShotsTotal,
    expectedShotsSoFar,
    adherencePct,
    completionPct,
    elapsedDays: Math.round(elapsedDays),
    totalDays: totalDays != null ? Math.round(totalDays) : null,
  };
}

export default function Cycles() {
  const [cycles, setCycles] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [shots, setShots] = useState([]);
  const [templates, setTemplates] = useState([]);
  const templateNameByKey = useMemo(
    () => Object.fromEntries((templates || []).map((t) => [t.key, t.name])),
    [templates]
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);

  // First-open auto-launch of the cycle mini-tour (only when creating, not editing)
  useEffect(() => {
    if (!dialogOpen || editing) return;
    try {
      if (!localStorage.getItem(CYCLE_TOUR_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [dialogOpen, editing]);

  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(CYCLE_TOUR_KEY, "1"); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    try {
      const [c, p, s, t] = await Promise.all([
        api.get("/cycles"), api.get("/peptides"), api.get("/shots"), api.get("/cycles/templates"),
      ]);
      setCycles(c.data);
      setPeptides(p.data);
      setShots(s.data);
      setTemplates(t.data?.templates || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      peptide_name: c.peptide_name,
      start_date: new Date(c.start_date).toISOString().slice(0, 10),
      end_date: c.end_date ? new Date(c.end_date).toISOString().slice(0, 10) : "",
      dose_mg: c.dose_mg != null ? String(c.dose_mg) : "",
      frequency: c.frequency || "",
      weekly_target_mg: c.weekly_target_mg != null ? String(c.weekly_target_mg) : "",
      notes: c.notes || "",
      active: c.active,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.peptide_name) { toast.error("Choose a peptide"); return; }
    setSaving(true);
    try {
      const payload = {
        peptide_name: form.peptide_name,
        start_date: new Date(form.start_date + "T12:00:00").toISOString(),
        end_date: form.end_date ? new Date(form.end_date + "T12:00:00").toISOString() : null,
        dose_mg: form.dose_mg === "" ? null : parseFloat(form.dose_mg),
        frequency: form.frequency || null,
        weekly_target_mg: form.weekly_target_mg === "" ? null : parseFloat(form.weekly_target_mg),
        notes: form.notes || null,
        active: form.active,
      };
      if (editing) {
        await api.put(`/cycles/${editing.id}`, payload);
        toast.success("Cycle updated");
      } else {
        await api.post("/cycles", payload);
        toast.success("Cycle added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/cycles/${confirmDelete.id}`);
      toast.success("Cycle deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const progressByCycle = useMemo(() => {
    const map = {};
    for (const c of cycles) map[c.id] = computeCycleProgress(c, shots);
    return map;
  }, [cycles, shots]);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Protocols"
        title="Cycles"
        subtitle={`Plan and monitor peptide cycles. ${cycles.filter(c => c.active).length} active · progress derived from logged shots.`}
        icon={<Repeat className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <CycleTemplatesPicker onStarted={load} />
            <Button onClick={openNew}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-11 px-6 uppercase tracking-widest font-semibold"
              data-testid="add-cycle-button">
              <Plus className="w-4 h-4 mr-2" /> New Cycle
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex justify-end">
        <Button
          variant="outline"
          onClick={() => downloadCsv("/export/cycles.csv", "amino-trade-cycles.csv")}
          className="border-slate-200 bg-transparent hover:bg-slate-50 hover:text-white text-slate-800 rounded-none h-10 uppercase tracking-widest text-xs font-semibold"
          data-testid="cycles-export-csv"
        >
          <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
        </Button>
      </div>

      {cycles.length === 0 ? (
        <div className="bg-white border border-slate-200 p-16 text-center">
          <div className="font-display font-black text-3xl uppercase text-slate-500">No cycles yet</div>
          <p className="text-slate-500 mt-2 text-sm">Start a new cycle to track a peptide protocol from start to finish.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cycles-grid">
          {cycles.map((c) => {
            const p = progressByCycle[c.id] || {};
            const pct = p.completionPct;
            const adherence = p.adherencePct;
            const barColor = adherence == null
              ? "#0D9488"
              : adherence < 70
                ? "#FF3B30"
                : adherence <= 115
                  ? "#22C55E"
                  : "#0D9488";
            return (
              <div key={c.id} className="bg-white border border-slate-200 p-5 hover:border-slate-300 transition-colors" data-testid={`cycle-card-${c.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
                      {c.active ? <span className="text-[#0D9488]">// Active</span> : <span className="text-slate-500">// Completed</span>}
                    </div>
                    <div className="font-display font-black text-2xl uppercase tracking-tight">{c.peptide_name}</div>
                    {c.template_key && (
                      <div className="mt-1.5 inline-flex items-center gap-1 border border-teal-800/60 text-teal-400 px-1.5 py-0.5 font-mono-num text-[9px] uppercase tracking-widest" data-testid={`cycle-template-badge-${c.id}`}>
                        <Sparkles className="w-2.5 h-2.5" />
                        <span>From {templateNameByKey[c.template_key] || c.template_key}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)}
                      className="h-8 w-8 hover:bg-slate-200 text-slate-600 hover:text-white rounded-none"
                      data-testid={`edit-cycle-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(c)}
                      className="h-8 w-8 hover:bg-slate-200 text-slate-600 hover:text-[#FF3B30] rounded-none"
                      data-testid={`delete-cycle-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                  <div>
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Start</div>
                    <div className="font-mono-num text-slate-900">{fmtDate(c.start_date)}</div>
                  </div>
                  <div>
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">End</div>
                    <div className="font-mono-num text-slate-900">{fmtDate(c.end_date)}</div>
                  </div>
                  {c.dose_mg != null && (
                    <div>
                      <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Dose</div>
                      <div className="font-mono-num text-slate-900">{c.dose_mg} mg</div>
                    </div>
                  )}
                  {c.frequency && (
                    <div>
                      <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Frequency</div>
                      <div className="font-mono-num text-slate-900">{c.frequency}</div>
                    </div>
                  )}
                </div>

                {/* Live shot metrics */}
                <div className="grid grid-cols-2 gap-2 py-3 border-t border-slate-200">
                  <div className="flex items-center gap-2">
                    <Syringe className="w-3 h-3 text-[#0D9488]" />
                    <div>
                      <div className="font-mono-num text-[9px] text-slate-500 uppercase tracking-widest">Shots</div>
                      <div className="font-mono-num font-black text-lg text-slate-900 tabular-nums" data-testid={`cycle-shots-taken-${c.id}`}>
                        {p.shotsTaken ?? 0}
                        {p.expectedShotsTotal != null && (
                          <span className="text-slate-500 text-xs font-normal"> / {Math.round(p.expectedShotsTotal)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-3 h-3 text-[#0D9488]" />
                    <div>
                      <div className="font-mono-num text-[9px] text-slate-500 uppercase tracking-widest">Dosed</div>
                      <div className="font-mono-num font-black text-lg text-slate-900 tabular-nums" data-testid={`cycle-mg-dosed-${c.id}`}>
                        {(p.mgDosed ?? 0).toFixed(2)}<span className="text-slate-500 text-xs font-normal"> mg</span>
                      </div>
                    </div>
                  </div>
                </div>

                {pct != null && (
                  <div>
                    <div className="flex justify-between font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                      <span>Progress</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-50 border border-slate-200">
                      <div className="h-full transition-[width]" style={{ width: `${pct}%`, background: barColor }} />
                    </div>
                    <div className="font-mono-num text-[10px] text-slate-500 mt-1.5 uppercase tracking-widest flex items-center justify-between gap-2">
                      <span>
                        {p.totalDays != null ? `Day ${p.elapsedDays} of ${p.totalDays}` : `Day ${p.elapsedDays}`}
                      </span>
                      {adherence != null && (
                        <span
                          className={
                            adherence < 70 ? "text-[#FF3B30]" :
                            adherence <= 115 ? "text-[#22C55E]" : "text-[#0D9488]"
                          }
                          data-testid={`cycle-adherence-${c.id}`}
                        >
                          {adherence}% adherence
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {c.notes && (
                  <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600 line-clamp-2">
                    {c.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-lg" data-testid="cycle-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black">
              {editing ? "Edit Cycle" : "New Cycle"}
            </DialogTitle>
            <DialogDescription className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">
              // {editing ? "update protocol" : "start a peptide protocol"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="cycle-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Peptide</Label>
              <Select value={form.peptide_name} onValueChange={(v) => setForm({ ...form, peptide_name: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="cycle-peptide">
                  <SelectValue placeholder="Select from library" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 rounded-none max-h-[300px]">
                  {peptides.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Start Date</Label>
                <Input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="cycle-start" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="cycle-end" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Dose (mg)</Label>
                <Input type="number" step="any" value={form.dose_mg} onChange={(e) => setForm({ ...form, dose_mg: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="cycle-dose" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Frequency</Label>
                <Input value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  placeholder="e.g., daily, EOD, 2x/week"
                  className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="cycle-frequency" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Weekly Target (mg, optional)</Label>
              <Input type="number" step="any" value={form.weekly_target_mg} onChange={(e) => setForm({ ...form, weekly_target_mg: e.target.value })}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="cycle-weekly-target" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="cycle-notes" />
            </div>

            <div className="flex items-center justify-between p-3 border border-slate-200 bg-slate-50">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })}
                className="data-[state=checked]:bg-[#0D9488]" data-testid="cycle-active" />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}
                className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800" data-testid="cycle-cancel">Cancel</Button>
              <Button type="submit" disabled={saving}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
                data-testid="cycle-save">
                {saving ? "Saving…" : (editing ? "Update" : "Start Cycle")}
              </Button>
            </DialogFooter>
          </form>
          <ProductTour steps={CYCLE_TOUR_STEPS} open={tourOpen} onClose={closeTour} />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete cycle?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will remove the {confirmDelete?.peptide_name} cycle from your protocols.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="cycle-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="cycle-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
